import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Typography } from '@mui/material';
import { useEffect, useRef, useContext, Children } from 'react';
import { HelixPalette, HelixStyles } from '../../helix';
import { DropdownMenuItem } from '../Dropdown/Dropdown.components';
import { IChevronRight } from '../Iconography';
import { MenuContext } from './MenuContext';
/**
 * Menu.Item component - represents a single menu item
 */
export const MenuItem = ({ dataId, style, icon: Icon, label, onSelect, includeChevron, children }) => {
    const context = useContext(MenuContext);
    const menuItemRef = useRef(null);
    if (!context) {
        throw new Error('Menu.Item must be used within a Menu component');
    }
    const { onClose, navigateToSubMenu, firstMenuItemId, focusedItemId, setFocusedItemId, focusedElementType } = context;
    // Support both direct nesting and lazy loading
    const isLazyLoaded = typeof children === 'function';
    const hasDirectChildren = !isLazyLoaded && Children.count(children) > 0;
    const hasChildren = isLazyLoaded || hasDirectChildren;
    const shouldShowChevron = includeChevron || hasChildren;
    const menuItemId = `${label.toLowerCase().replace(/\s+/g, '-')}`;
    // Focus this item if it's marked as the first one or currently focused
    // but only if we're in menu-item focus mode (not back-button mode)
    useEffect(() => {
        if (focusedElementType === 'menu-item' && (firstMenuItemId === menuItemId || focusedItemId === menuItemId) && menuItemRef.current) {
            menuItemRef.current.focus({ preventScroll: true });
        }
    }, [firstMenuItemId, focusedItemId, menuItemId, focusedElementType]);
    const handleClick = (event) => {
        if (hasChildren && children) {
            // Handle both lazy loading and direct nesting
            const subMenuContent = isLazyLoaded ? children() : children;
            navigateToSubMenu(label, subMenuContent);
        }
        else {
            // Execute onSelect and close menu
            if (onSelect) {
                onSelect(event);
            }
            onClose();
        }
    };
    const handleFocus = () => {
        // Only update focused item if we're in menu-item focus mode
        if (focusedElementType === 'menu-item') {
            setFocusedItemId(menuItemId);
        }
    };
    return (_jsx(DropdownMenuItem, Object.assign({ ref: menuItemRef, "data-id": `${dataId}-menu-button`, "aria-labelledby": `${dataId}-menu-item-label`, onClick: handleClick, onFocus: handleFocus, tabIndex: focusedItemId === menuItemId ? 0 : -1, role: "menuitem", disableRipple: true, style: Object.assign({}, style) }, { children: _jsxs(Box, Object.assign({ sx: {
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flex: 1,
                color: HelixPalette.neutral90,
            } }, { children: [Icon && _jsx(Icon, { dataId: `${dataId}-menu-item-icon`, color: HelixPalette.neutral70 }), _jsx(Typography, Object.assign({ "data-id": `${dataId}-menu-item-label`, style: Object.assign(Object.assign({}, HelixStyles.style_07), { color: HelixPalette.neutral90, flex: 1 }) }, { children: label })), shouldShowChevron && _jsx(IChevronRight, { dataId: `${dataId}-menu-item-chevron` })] })) }), menuItemId));
};
